import { z } from "zod";
import {
  cursorPaginatedResponseSchema,
  DateSchema,
  IdSchema,
  NameSchema,
  NonNegativeNumber,
  PhoneSchema,
} from "./commonSchema";
import type { CursorPaginatedResponse } from "@/types/common";

export const ClientSchema = z.object({
  id: IdSchema,
  name: NameSchema,
  phone: PhoneSchema.nullish(),
  email: z.string().nullish(),
  workshopId: IdSchema,
  isFlagged: z.boolean().default(false),
  flagReason: z.string().nullish(),
  createdBy: IdSchema.nullish(),
  createdAt: DateSchema,
  createdByName: NameSchema.nullish(),
  updatedAt: DateSchema,
  deletedAt: DateSchema.nullish(),
});

export type Client = z.infer<typeof ClientSchema>;

export const ClientListItemSchema = ClientSchema.pick({
  id: true,
  name: true,
  phone: true,
  email: true,
  isFlagged: true,
  flagReason: true,
  createdAt: true,
  deletedAt: true,
  createdByName: true,
});

export type ClientListItem = z.infer<typeof ClientListItemSchema>;

export const CreateClientSchema = ClientSchema.pick({
  name: true,
  phone: true,
  email: true,
});

export type CreateClient = z.infer<typeof CreateClientSchema>;

export const ClientStatsSchema = z.object({
  goldBalance: z.coerce.number(),
  cashBalance: z.coerce.number(),
  inboundPureWeight: NonNegativeNumber,
  totalWeightMoved: NonNegativeNumber,
  totalLabor: NonNegativeNumber,
  totalDeposit: NonNegativeNumber,
  lastTransactionAt: z.string().nullable(),
});

export type ClientStats = z.infer<typeof ClientStatsSchema>;

export type ClientListResponse = CursorPaginatedResponse<ClientListItem>;

export const ClientListResponseSchema = cursorPaginatedResponseSchema(ClientListItemSchema);

export const FlagClientSchema = z.object({
  reason: z.string().optional(),
});

export type FlagClientForm = z.infer<typeof FlagClientSchema>;

export const FlagClientPayloadSchema = ClientSchema.pick({
  isFlagged: true,
  flagReason: true,
});

export type FlagClientPayload = z.infer<typeof FlagClientPayloadSchema>;

export const ClientBalanceSchema = z.object({
  clientId: IdSchema,
  clientName: z.string(),
  goldBalance: z.coerce.number(),
  cashBalance: z.coerce.number(),
  inboundPureWeight: NonNegativeNumber,
  lastTransactionAt: z.string().nullable(),
});

export type ClientBalance = z.infer<typeof ClientBalanceSchema>;

export const ClientsStatsSchema = z.object({
  clientsWithDebt: z.array(ClientBalanceSchema),
  newClientsThisMonth: z.array(ClientBalanceSchema),
  flaggedClients: z.array(ClientBalanceSchema),
});

export type ClientsStats = z.infer<typeof ClientsStatsSchema>;
